import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Truck, Menu, User, LogOut, LayoutDashboard, FileText, Building2, Users } from "lucide-react";
import { useState } from "react";

export default function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const { settings } = useSiteSettings();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getDashboardLink = () => {
    if (!user) return "/dashboard";
    if (user.role === "admin") return "/admin";
    if (user.role === "company") return "/company";
    return "/dashboard";
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt="Logo" className="h-10 object-contain" />
            ) : (
              <>
                <div className="bg-primary p-2 group-hover:bg-primary/90 transition-colors">
                  <Truck className="h-6 w-6 text-primary-foreground" />
                </div>
                <span className="font-bold text-xl tracking-tight hidden sm:block" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  NOLEGGIO<span className="text-primary">FURGONI</span>.IT
                </span>
              </>
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/catalogo" className="text-sm font-medium uppercase tracking-wider hover:text-primary transition-colors">
              Catalogo
            </Link>
            {isAuthenticated && (
              <Link to={getDashboardLink()} className="text-sm font-medium uppercase tracking-wider hover:text-primary transition-colors">
                Dashboard
              </Link>
            )}
          </nav>

          {/* Auth Buttons / User Menu */}
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold">
                        {user?.name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                    <p className="text-xs text-primary uppercase mt-1">{user?.role}</p>
                  </div>
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem onClick={() => navigate(getDashboardLink())} data-testid="menu-dashboard">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </DropdownMenuItem>
                  
                  {user?.role === "customer" && (
                    <DropdownMenuItem onClick={() => navigate("/prenotazioni")} data-testid="menu-bookings">
                      <FileText className="mr-2 h-4 w-4" />
                      Le mie prenotazioni
                    </DropdownMenuItem>
                  )}
                  
                  {user?.role === "company" && (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/company/veicoli")} data-testid="menu-vehicles">
                        <Truck className="mr-2 h-4 w-4" />
                        I miei veicoli
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/company/prenotazioni")} data-testid="menu-company-bookings">
                        <FileText className="mr-2 h-4 w-4" />
                        Prenotazioni
                      </DropdownMenuItem>
                    </>
                  )}
                  
                  {user?.role === "admin" && (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/admin/utenti")} data-testid="menu-admin-users">
                        <Users className="mr-2 h-4 w-4" />
                        Utenti
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/aziende")} data-testid="menu-admin-companies">
                        <Building2 className="mr-2 h-4 w-4" />
                        Aziende
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/prenotazioni")} data-testid="menu-admin-bookings">
                        <FileText className="mr-2 h-4 w-4" />
                        Prenotazioni
                      </DropdownMenuItem>
                    </>
                  )}
                  
                  <DropdownMenuItem onClick={() => navigate("/profilo")} data-testid="menu-profile">
                    <User className="mr-2 h-4 w-4" />
                    Profilo
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => navigate("/login")} data-testid="login-btn">
                  Accedi
                </Button>
                <Button onClick={() => navigate("/register")} className="btn-primary py-2 px-4" data-testid="register-btn">
                  Registrati
                </Button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-6 w-6" />
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <nav className="flex flex-col gap-2">
              <Link
                to="/catalogo"
                className="px-4 py-2 text-sm font-medium uppercase tracking-wider hover:bg-accent"
                onClick={() => setMobileMenuOpen(false)}
              >
                Catalogo
              </Link>
              {isAuthenticated && (
                <Link
                  to={getDashboardLink()}
                  className="px-4 py-2 text-sm font-medium uppercase tracking-wider hover:bg-accent"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
